<template>
  <div class="lad-tabs" :class="{ 'sticky-active': stickyActive }">
    <div class="lad-el-tabs el-tabs" :class="[`el-tabs--${position}`]">
      <div class="el-tabs__header" :class="[`is-${position}`]">
        <div class="el-tabs__nav-wrap" :class="[`is-${position}`]">
          <div class="el-tabs__nav-scroll">
            <div
              class="el-tabs__nav"
              :class="[`is-${position}`]"
              role="tablist"
            >
              <div
                class="el-tabs__active-bar"
                :class="[`is-${position}`]"
                :style="activeBarStyle"
              ></div>
              <template v-if="customTabs">
                <div
                  v-for="(tab, i) in tabs"
                  :id="`tab-${tab.slug}`"
                  :key="tab.slug"
                  :aria-controls="`pane-${tab.slug}`"
                  role="tab"
                  aria-selected="true"
                  :tabindex="i"
                  class="el-tabs__item"
                  :class="[
                    `is-${position}`,
                    tab.slug === currentStep ? 'is-active' : '',
                  ]"
                  @click.prevent="changeTab(tab.slug, i)"
                  >{{ tab.title }}</div
                >
              </template>
              <template v-else>
                <div
                  v-for="(tab, i) in tabs"
                  :id="`tab-${tab}`"
                  :key="tab"
                  :aria-controls="`pane-${tab}`"
                  role="tab"
                  aria-selected="true"
                  :tabindex="i"
                  class="el-tabs__item"
                  :class="[
                    `is-${position}`,
                    tab === currentStep ? 'is-active' : '',
                  ]"
                  @click.prevent="changeTab(tab, i)"
                  >{{
                    $t(
                      `${base}.${tab.replace(/-/g, '_')}`,
                      // eslint-disable-next-line vue/comma-dangle
                      Array.isArray(tabParams) ? tabParams[i] : tabParams
                    )
                  }}</div
                >
              </template>
            </div>
          </div>
        </div>
      </div>

      <div class="el-tabs__content">
        <slot />
      </div>
    </div>

    <el-dropdown
      v-if="isEnableDropdown"
      trigger="click"
      @command="handleCommand"
    >
      <el-button icon="fas fa-ellipsis-v" circle></el-button>

      <el-dropdown-menu slot="dropdown">
        <template v-if="customTabs">
          <el-dropdown-item
            v-for="tab in tabs"
            :key="tab.slug"
            :command="tab.slug"
            >{{ tab.title }}</el-dropdown-item
          >
        </template>

        <template v-else>
          <el-dropdown-item v-for="tab in tabs" :key="tab" :command="tab">{{
            $t(`${base}.${tab.replace(/-/g, '_')}`)
          }}</el-dropdown-item>
        </template>
      </el-dropdown-menu>
    </el-dropdown>
  </div>
</template>

<script>
import { tabScrollLeft, ladFnCapitalize, debounce } from '@/utils'

export default {
  name: 'LadAdvancedTabs',
  props: {
    base: { type: String, required: true },
    tabs: { type: Array, required: true },
    // eslint-disable-next-line vue/require-prop-type-constructor
    tabParams: { type: Object | Array, default: () => ({}) },
    customTabs: { type: Boolean, default: false },
    tabsId: { type: String, required: true },
    defaultStep: { type: String, required: true },
    withSticky: { type: Boolean, default: false },
    withAutoScroll: { type: Boolean, default: false },
    stickyOptions: {
      type: Object,
      default: () => ({ relative: 'body', top: 60 }),
    },
    position: { type: String, default: 'top' },
  },
  data() {
    return {
      currentStep: null,
      isEnableDropdown: false,
      stickyActive: false,
      activeBarStyle: {
        width: 0,
        transform: 'translateX(0)',
      },
    }
  },
  computed: {
    refName() {
      const name = this.tabsId.split('_').map(ladFnCapitalize).join('')

      return name
    },
  },
  watch: {
    defaultStep: {
      immediate: true,
      handler(x, y) {
        if ((!!x && !this.currentStep) || x !== this.currentStep) {
          this.currentStep = x
        }
      },
    },
    currentStep(value) {
      this.$emit('current-tab', value)

      this.scrollTo()

      this.updateStyleSelected()
    },
  },
  beforeMount() {
    this.__handleResize = debounce(this.enableDropdown, 100)

    window.addEventListener('resize', this.__handleResize)
  },
  mounted() {
    console.log('advancedtab before component mounted')
    this.LadNextTick(this.__handleResize, 'LadAdvancedTabs')
    console.log('advancedtab after component mounted')

    if (this.withSticky) {
      /** https://www.w3schools.com/howto/howto_js_sticky_header.asp */
      window.onscroll = () => {
        const wrapper = document.querySelector(this.stickyOptions.relative)
        if (!wrapper) return

        const tabs = wrapper.querySelector('.lad-el-tabs > .el-tabs__header')

        if (window.pageYOffset > wrapper.offsetTop + this.stickyOptions.top) {
          try {
            tabs.classList.add('sticky')
          } catch (error) {}
          this.stickyActive = true
        } else {
          try {
            tabs.classList.remove('sticky')
          } catch (error) {}
          this.stickyActive = false
        }

        if (
          window.pageYOffset >
          wrapper.offsetTop + wrapper.clientHeight + 20
        ) {
          try {
            tabs.classList.add('sticky-full')
          } catch (error) {}
        } else {
          try {
            tabs.classList.remove('sticky-full')
          } catch (error) {}
        }
      }
    }

    this.updateStyleSelected()
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.__handleResize)
  },
  methods: {
    enableDropdown() {
      console.log('after enableDropdown')
      const tabsWidth = Array.from(
        this.$el.querySelectorAll(
          '.lad-el-tabs > .el-tabs__header .el-tabs__item'
        )
      ).reduce((prev, current, index) => {
        const width = current.offsetWidth

        return prev + width
      }, 0)

      const wrapperWidth = this.$el.querySelector(
        '.lad-el-tabs > .el-tabs__header .el-tabs__nav'
      ).offsetWidth

      this.isEnableDropdown = tabsWidth > wrapperWidth
      console.log('before enableDropdown')
    },
    handleClick(tab, event) {
      tabScrollLeft(this.$refs[this.refName], this.currentStep)

      this.$emit('tab-click', { tab, event })
    },
    handleCommand(command) {
      this.currentStep = command

      tabScrollLeft(this.$refs[this.refName], this.currentStep)
    },
    changeTab(tab) {
      this.currentStep = tab
    },
    scrollTo() {
      if (this.withAutoScroll) {
        this.$scrollTo('.lad-tabs .el-tabs__content', 500, {
          offset: -120,
        })
      }
    },
    updateStyleSelected() {
      const items = Array.from(this.$el.querySelectorAll('.el-tabs__item'))
      const total = items.length - 1

      items.forEach((item, i) => {
        const key = (item.id || '').replace('tab-', '')

        if (key === this.currentStep) {
          let width = item.clientWidth || item.offsetWidth
          let paddings = 0

          try {
            const prop = window.getComputedStyle(item, null)

            paddings += parseInt(prop.paddingLeft)
            paddings += parseInt(prop.paddingRight)
          } catch (e) {}

          const divider = i === 0 || i === total ? 1 : 2
          const left = i === 0 ? 0 : item.offsetLeft + paddings / divider
          width -= paddings

          this.activeBarStyle.width = `${width}px`
          this.activeBarStyle.transform = `translateX(${left}px)`
        }
      })
    },
  },
}
</script>

<style lang="scss">
.lad-tabs {
  position: relative;

  .lad-el-tabs {
    position: relative;

    .el-tabs__nav-wrap {
      .el-tabs__nav {
        float: none;
        margin: 0 20px;
        scrollbar-width: none;

        /* Make this scrollable when needed */
        overflow-x: auto;

        /* We don't want vertical scrolling */
        overflow-y: hidden;

        /* Make an auto-hiding scroller for the 3 people using a IE */
        -ms-overflow-style: -ms-autohiding-scrollbar;

        /* For WebKit implementations, provide inertia scrolling */
        -webkit-overflow-scrolling: touch;

        /* We don't want internal inline elements to wrap */
        white-space: nowrap;

        /* Remove the default scrollbar for WebKit implementations */
        &::-webkit-scrollbar {
          display: none;
        }
      }

      &.is-scrollable {
        .el-tabs__nav {
          margin-left: 0;
          margin-right: 0;
        }
      }

      &::after {
        background-color: transparent;
      }
    }

    & > .el-tabs__header {
      margin-bottom: 20px;
      position: absolute;
      width: 100%;

      @media (min-width: 768px) {
        background-color: unset;
      }
    }

    & > .el-tabs__content {
      padding-top: 60px;
    }

    /** dropdow laterl */
    & + .el-dropdown {
      position: absolute;
      z-index: 60;
      top: 0;
      right: 0;
      line-height: 40px;

      .el-button {
        border: 0;

        .fas {
          max-width: 14px;
          max-height: 14px;
          width: 14px;
          height: 14px;
        }

        &:active,
        &:focus,
        &:hover {
          background-color: inherit;
          color: inherit;
        }
      }
    }

    @media screen and (max-width: 1026px) {
      .el-tabs__nav-wrap {
        .el-tabs__nav {
          margin-right: 40px;
        }
      }

      & > .el-tabs__header {
        .el-tabs__item {
          height: 50px;
          line-height: 50px;
          font-size: 16px;
        }

        .el-tabs__active-bar {
          height: 4px;
        }
      }

      & + .el-dropdown {
        line-height: 50px;

        // .el-button {
        //   padding: 10px 18px;
        // }
      }
    }
  }

  &.sticky-active {
    .lad-el-tabs {
      & > .el-tabs__content {
        padding-top: 0;
      }

      & + .el-dropdown {
        position: fixed;
      }

      & > .el-tabs__header {
        background-color: white;
      }
    }
  }
}
</style>
